import React, { useEffect, useRef, useState } from "react";
import GuidedTour from "../../components/GuidedTour";
import { tourStepsMissingNumber } from "./tourStepsMissingNumber";
import { useNavigate } from "react-router-dom";
import BubbleText from "../../components/BubbleText";
import GamePageToolbar from "../../components/GamePageToolbar";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Collection,
  GamePair,
  deleteCollection,
  getGameCollections,
  saveGameCollection,
} from "../../api";

type PatternMode = "countBy1" | "skipCount" | "mixed" | "custom";

const PATTERN_OPTIONS: { id: PatternMode; label: string }[] = [
  { id: "countBy1", label: "Counting by 1s" },
  { id: "skipCount", label: "Skip counting (2s, 5s, 10s)" },
  { id: "mixed", label: "Mixed" },
];

const HIDDEN_PATTERNS_KEY = "missingNumberHiddenPatterns";

const readHiddenPatternIds = (): PatternMode[] => {
  try {
    const stored = localStorage.getItem(HIDDEN_PATTERNS_KEY);
    const parsed = stored ? (JSON.parse(stored) as string[]) : [];
    return parsed.filter(
      (id): id is PatternMode =>
        id === "countBy1" || id === "skipCount" || id === "mixed",
    );
  } catch {
    return [];
  }
};

interface SequenceProblem {
  values: number[];
  blankIndex: number;
  answer: number;
}

const randomInt = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const generateProblem = (mode: PatternMode): SequenceProblem => {
  let step = 1;
  if (mode === "skipCount") {
    step = [2, 5, 10][randomInt(0, 2)];
  } else if (mode === "mixed") {
    step = [1, 2, 5, 10][randomInt(0, 3)];
  }

  const goingDown = mode !== "countBy1" && Math.random() < 0.25;
  const signedStep = goingDown ? -step : step;
  const length = randomInt(3, 5);
  const blankIndex = randomInt(0, length - 1);

  const minStart =
    signedStep > 0 ? 0 : Math.abs(signedStep) * (length - 1);
  const maxStart =
    signedStep > 0 ? 100 - signedStep * (length - 1) : 100;
  const start = randomInt(minStart, Math.max(minStart, maxStart));
  const values = Array.from(
    { length },
    (_, index) => start + index * signedStep,
  );

  return {
    values,
    blankIndex,
    answer: values[blankIndex],
  };
};

const parseSequenceInput = (text: string): number[] | null => {
  const parts = text
    .split(/[,\s]+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length < 2) {
    return null;
  }

  const values = parts.map(Number);
  if (values.some((value) => Number.isNaN(value))) {
    return null;
  }

  return values;
};

const formatSequencePreview = (problem: SequenceProblem) =>
  problem.values
    .map((value, index) => (index === problem.blankIndex ? "__" : String(value)))
    .join(", ");

const sequencesToGamePairs = (sequences: SequenceProblem[]): GamePair[] =>
  sequences.map((sequence) => ({
    word: sequence.values.join(", "),
    imageUrl: String(sequence.blankIndex),
  }));

const parseSavedSequences = (description: string): SequenceProblem[] => {
  try {
    let parsed: unknown = JSON.parse(description);
    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }
    if (!Array.isArray(parsed)) {
      return [];
    }

    return (parsed as GamePair[])
      .map((item) => {
        const values = parseSequenceInput(item.word ?? "");
        const blankIndex = Number(item.imageUrl);
        if (
          !values ||
          Number.isNaN(blankIndex) ||
          blankIndex < 0 ||
          blankIndex >= values.length
        ) {
          return null;
        }
        return {
          values,
          blankIndex,
          answer: values[blankIndex],
        };
      })
      .filter((sequence): sequence is SequenceProblem => sequence !== null);
  } catch {
    return [];
  }
};

const MissingNumber: React.FC = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const inputRef = useRef<HTMLInputElement>(null);
  const nextProblemTimeoutRef = useRef<number | null>(null);

  const [isGameStarted, setIsGameStarted] = useState(false);
  const [hiddenPatternIds, setHiddenPatternIds] = useState<PatternMode[]>(
    readHiddenPatternIds,
  );
  const [patternMode, setPatternMode] = useState<PatternMode>(() => {
    const hidden = readHiddenPatternIds();
    const visible = PATTERN_OPTIONS.find(
      (option) => !hidden.includes(option.id),
    );
    return visible?.id ?? "custom";
  });
  const [customSequences, setCustomSequences] = useState<SequenceProblem[]>([]);
  const [sequenceDraft, setSequenceDraft] = useState("");
  const [blankIndexDraft, setBlankIndexDraft] = useState<number | null>(null);
  const [customIndex, setCustomIndex] = useState(0);
  const [savedSets, setSavedSets] = useState<Collection[]>([]);
  const [setName, setSetName] = useState("");
  const [selectedSavedSetId, setSelectedSavedSetId] = useState("");
  const [problem, setProblem] = useState<SequenceProblem>(() => {
    const hidden = readHiddenPatternIds();
    const visible = PATTERN_OPTIONS.find(
      (option) => !hidden.includes(option.id),
    );
    return generateProblem(visible?.id ?? "mixed");
  });
  const [guess, setGuess] = useState("");
  const [feedback, setFeedback] = useState<"correct" | "incorrect" | null>(
    null,
  );
  const [score, setScore] = useState(0);
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [currentTourStep, setCurrentTourStep] = useState(0);

  useEffect(() => {
    const tourCompleted = localStorage.getItem("tourCompleted_missingNumber");
    if (!tourCompleted) {
      setIsTourRunning(true);
    }
  }, []);

  useEffect(() => {
    if (isGameStarted && feedback !== "correct") {
      inputRef.current?.focus();
    }
  }, [isGameStarted, problem, feedback]);

  const loadSavedSets = async () => {
    try {
      const collections = await getGameCollections(
        "missing_number",
        getAccessTokenSilently,
      );
      setSavedSets(collections ?? []);
    } catch (error) {
      console.error("Failed to load saved sequences:", error);
    }
  };

  useEffect(() => {
    loadSavedSets();
  }, [getAccessTokenSilently]);

  const clearNextProblemTimeout = () => {
    if (nextProblemTimeoutRef.current !== null) {
      window.clearTimeout(nextProblemTimeoutRef.current);
      nextProblemTimeoutRef.current = null;
    }
  };

  const draftValues = parseSequenceInput(sequenceDraft);
  const canAddCustomSequence =
    draftValues !== null &&
    blankIndexDraft !== null &&
    blankIndexDraft >= 0 &&
    blankIndexDraft < draftValues.length;
  const canStart =
    patternMode !== "custom" || customSequences.length > 0;
  const visiblePatternOptions = PATTERN_OPTIONS.filter(
    (option) => !hiddenPatternIds.includes(option.id),
  );

  const persistHiddenPatternIds = (nextIds: PatternMode[]) => {
    setHiddenPatternIds(nextIds);
    localStorage.setItem(HIDDEN_PATTERNS_KEY, JSON.stringify(nextIds));
  };

  const handleHidePattern = (patternId: PatternMode) => {
    if (patternId === "custom") {
      return;
    }
    if (
      !window.confirm(
        "Remove this example from the list? You can restore examples later.",
      )
    ) {
      return;
    }
    const nextHiddenIds = hiddenPatternIds.includes(patternId)
      ? hiddenPatternIds
      : [...hiddenPatternIds, patternId];
    persistHiddenPatternIds(nextHiddenIds);
    if (patternMode === patternId) {
      const nextVisible = PATTERN_OPTIONS.find(
        (option) => !nextHiddenIds.includes(option.id),
      );
      setPatternMode(nextVisible?.id ?? "custom");
    }
  };

  const handleRestorePatterns = () => {
    persistHiddenPatternIds([]);
  };

  const addCustomSequence = () => {
    if (!draftValues || blankIndexDraft === null) {
      return;
    }

    setCustomSequences((currentSequences) => [
      ...currentSequences,
      {
        values: draftValues,
        blankIndex: blankIndexDraft,
        answer: draftValues[blankIndexDraft],
      },
    ]);
    setSequenceDraft("");
    setBlankIndexDraft(null);
  };

  const removeCustomSequence = (indexToRemove: number) => {
    setCustomSequences((currentSequences) =>
      currentSequences.filter((_, index) => index !== indexToRemove),
    );
  };

  const handleSaveSet = async () => {
    if (customSequences.length === 0) {
      alert("Add at least one sequence before saving.");
      return;
    }
    if (!setName.trim()) {
      alert("Please enter a name for this set.");
      return;
    }

    try {
      await saveGameCollection(
        setName.trim(),
        sequencesToGamePairs(customSequences),
        "missing_number",
        getAccessTokenSilently,
      );
      setSetName("");
      await loadSavedSets();
      alert("Sequences saved. You can load them later.");
    } catch (error) {
      console.error("Failed to save sequences:", error);
      alert("Failed to save sequences. Please try again.");
    }
  };

  const handleLoadSavedSet = (collectionId: string) => {
    setSelectedSavedSetId(collectionId);
    const selected = savedSets.find(
      (collection) => String(collection.collection_id) === collectionId,
    );
    if (!selected) {
      return;
    }
    const sequences = parseSavedSequences(selected.description);
    setCustomSequences(sequences);
    setSetName(selected.name);
  };

  const handleDeleteSavedSet = async () => {
    const selected = savedSets.find(
      (collection) => String(collection.collection_id) === selectedSavedSetId,
    );
    if (!selected) {
      alert("Select a saved set to remove.");
      return;
    }
    if (!window.confirm("Remove this saved sequence set?")) {
      return;
    }

    try {
      await deleteCollection(selected.collection_id, getAccessTokenSilently);
      setSelectedSavedSetId("");
      await loadSavedSets();
    } catch (error) {
      console.error("Failed to delete sequences:", error);
      alert("Failed to remove saved sequences. Please try again.");
    }
  };

  const loadNextProblem = () => {
    clearNextProblemTimeout();
    if (patternMode === "custom") {
      if (customSequences.length === 0) {
        return;
      }
      const nextIndex = (customIndex + 1) % customSequences.length;
      setCustomIndex(nextIndex);
      setProblem(customSequences[nextIndex]);
    } else {
      setProblem(generateProblem(patternMode));
    }
    setGuess("");
    setFeedback(null);
  };

  const startGame = () => {
    if (!canStart) {
      return;
    }

    setScore(0);
    setGuess("");
    setFeedback(null);
    if (patternMode === "custom") {
      setCustomIndex(0);
      setProblem(customSequences[0]);
    } else {
      setProblem(generateProblem(patternMode));
    }
    setIsGameStarted(true);
  };

  const checkAnswer = () => {
    if (feedback === "correct") {
      return;
    }

    const parsedGuess = Number(guess.trim());
    if (guess.trim() === "" || Number.isNaN(parsedGuess)) {
      setFeedback("incorrect");
      return;
    }

    if (parsedGuess === problem.answer) {
      setFeedback("correct");
      setScore((currentScore) => currentScore + 1);
      clearNextProblemTimeout();
      nextProblemTimeoutRef.current = window.setTimeout(() => {
        loadNextProblem();
      }, 800);
      return;
    }

    setFeedback("incorrect");
  };

  const handleBack = () => {
    navigate("/games");
  };

  const goBackToSetup = () => {
    clearNextProblemTimeout();
    setIsGameStarted(false);
    setGuess("");
    setFeedback(null);
  };

  useEffect(() => {
    return () => clearNextProblemTimeout();
  }, []);

  const handleTourComplete = () => {
    setIsTourRunning(false);
    localStorage.setItem("tourCompleted_missingNumber", "true");
  };

  const blankClassName =
    feedback === "correct"
      ? "border-green-600 bg-green-100 text-green-800"
      : feedback === "incorrect"
        ? "border-red-500 bg-red-100 text-red-800"
        : "border-black bg-white text-black";

  return (
    <div className={`page-container text-theme-text`}>
      <GamePageToolbar onBack={handleBack}>
        {isGameStarted && (
          <>
            <button
              type="button"
              onClick={loadNextProblem}
              className="reset-button max-w-[300px] cursor-pointer rounded border border-black bg-yellow-500 px-3 py-2 text-base font-bold uppercase text-black transition duration-300 hover:scale-105 hover:bg-yellow-600 active:scale-95 active:bg-yellow-700"
            >
              Next Sequence
            </button>
            <button
              type="button"
              onClick={goBackToSetup}
              className="back-to-setup-button max-w-[300px] cursor-pointer rounded border border-black bg-blue-500 px-3 py-2 text-base font-bold uppercase text-white transition duration-300 hover:bg-blue-600"
            >
              Back to Setup
            </button>
          </>
        )}
      </GamePageToolbar>
      <h1 className="mb-8 text-3xl font-bold">
        <BubbleText>Missing Number</BubbleText>
      </h1>

      {!isGameStarted ? (
        <div className="game-instructions flex w-full max-w-2xl flex-col items-center">
          <p className="inherit mb-4 text-center">
            A number sequence will appear with a blank. Type the missing
            number, like 1, 2, __, 4.
          </p>
          <label htmlFor="patternMode" className="mb-2 block font-semibold">
            Number pattern
          </label>
          <select
            id="patternMode"
            value={patternMode}
            onChange={(event) =>
              setPatternMode(event.target.value as PatternMode)
            }
            className="mb-4 w-full rounded border border-black bg-white p-2 text-black"
          >
            {visiblePatternOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
            <option value="custom">Create your own</option>
          </select>
          {patternMode !== "custom" && (
            <button
              type="button"
              onClick={() => handleHidePattern(patternMode)}
              className="mb-4 rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
            >
              Remove Example
            </button>
          )}
          {hiddenPatternIds.length > 0 && (
            <button
              type="button"
              onClick={handleRestorePatterns}
              className="mb-4 text-sm underline"
            >
              Restore example patterns
            </button>
          )}

          {patternMode === "custom" && (
            <div className="mb-6 w-full rounded border border-black bg-white p-4 text-black">
              <label htmlFor="sequenceDraft" className="mb-2 block font-semibold">
                Enter a sequence
              </label>
              <input
                id="sequenceDraft"
                type="text"
                value={sequenceDraft}
                onChange={(event) => {
                  setSequenceDraft(event.target.value);
                  setBlankIndexDraft(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && canAddCustomSequence) {
                    addCustomSequence();
                  }
                }}
                placeholder="Example: 1, 2, 3, 4"
                className="mb-3 w-full rounded border border-black p-2"
              />
              {draftValues ? (
                <>
                  <p className="mb-2 text-sm">
                    Click the number that should be the blank.
                  </p>
                  <div className="mb-3 flex flex-wrap items-center justify-center gap-2 text-2xl font-bold">
                    {draftValues.map((value, index) => (
                      <React.Fragment key={`${value}-${index}`}>
                        <button
                          type="button"
                          onClick={() => setBlankIndexDraft(index)}
                          className={`min-w-12 rounded border-2 px-3 py-1 ${
                            blankIndexDraft === index
                              ? "border-blue-600 bg-blue-100"
                              : "border-black bg-white"
                          }`}
                        >
                          {blankIndexDraft === index ? "__" : value}
                        </button>
                        {index < draftValues.length - 1 && (
                          <span className="text-gray-500">,</span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </>
              ) : (
                <p className="mb-3 text-sm text-gray-600">
                  Type at least two numbers, separated by commas.
                </p>
              )}
              <button
                type="button"
                onClick={addCustomSequence}
                disabled={!canAddCustomSequence}
                className={`rounded border border-black bg-blue-500 px-4 py-2 text-white ${
                  canAddCustomSequence
                    ? "hover:bg-blue-600"
                    : "cursor-not-allowed opacity-50"
                }`}
              >
                Add Sequence
              </button>

              {customSequences.length > 0 && (
                <div className="mt-4 flex flex-col items-center">
                  <p className="mb-2 font-semibold">Your sequences</p>
                  <ul className="flex w-full flex-col gap-2">
                    {customSequences.map((customProblem, index) => (
                      <li
                        key={`${formatSequencePreview(customProblem)}-${index}`}
                        className="flex items-center justify-between rounded bg-gray-100 px-3 py-2"
                      >
                        <span>{formatSequencePreview(customProblem)}</span>
                        <button
                          type="button"
                          onClick={() => removeCustomSequence(index)}
                          className="ml-3 text-red-500 hover:text-red-700"
                          aria-label="Remove sequence"
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 flex w-full flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-center">
                    <input
                      type="text"
                      value={setName}
                      onChange={(event) => setSetName(event.target.value)}
                      placeholder="Name this set"
                      className="w-full rounded border border-black p-2 sm:max-w-xs"
                    />
                    <button
                      type="button"
                      onClick={handleSaveSet}
                      className="rounded border border-black bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                    >
                      Save Sequences
                    </button>
                  </div>
                </div>
              )}

              {savedSets.length > 0 && (
                <div className="mt-4 flex w-full flex-col items-center">
                  <p className="mb-2 font-semibold">Saved sets</p>
                  <div className="flex w-full flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-center">
                    <select
                      title="Load a saved sequence set"
                      value={selectedSavedSetId}
                      onChange={(event) => handleLoadSavedSet(event.target.value)}
                      className="w-full rounded border border-black p-2 sm:max-w-xs"
                    >
                      <option value="">Load a saved set</option>
                      {savedSets.map((collection) => (
                        <option
                          key={collection.collection_id}
                          value={collection.collection_id}
                        >
                          {collection.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleDeleteSavedSet}
                      className="rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
                    >
                      Remove Saved Set
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={startGame}
            disabled={!canStart}
            className={`start-button max-w-[300px] rounded border border-black bg-green-500 p-2.5 text-base font-bold uppercase text-white transition duration-300 hover:bg-green-600 ${
              canStart ? "cursor-pointer" : "cursor-not-allowed opacity-50"
            }`}
          >
            Start Game
          </button>
        </div>
      ) : (
        <div className="missing-number-game flex w-full max-w-3xl flex-col items-center">
          <p className="mb-2 text-lg font-semibold">Score: {score}</p>
          <p className="inherit mb-6">What number belongs in the blank?</p>
          <div className="mb-8 flex flex-wrap items-center justify-center gap-2 text-2xl font-bold sm:gap-3 sm:text-4xl">
            {problem.values.map((value, index) => (
              <React.Fragment key={`${value}-${index}`}>
                {index === problem.blankIndex ? (
                  <input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    aria-label="Missing number"
                    value={guess}
                    disabled={feedback === "correct"}
                    onChange={(event) => {
                      setGuess(event.target.value);
                      setFeedback(null);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        checkAnswer();
                      }
                    }}
                    className={`w-20 rounded border-4 p-2 text-center text-2xl outline-none sm:w-24 sm:text-4xl ${blankClassName}`}
                  />
                ) : (
                  <span>{value}</span>
                )}
                {index < problem.values.length - 1 && (
                  <span className="text-gray-500">,</span>
                )}
              </React.Fragment>
            ))}
          </div>
          <button
            type="button"
            onClick={checkAnswer}
            disabled={feedback === "correct"}
            className="min-h-[44px] w-full max-w-xs rounded border border-black bg-green-500 px-6 py-3 text-lg font-bold uppercase text-white transition duration-300 hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >
            Check
          </button>
          {feedback === "correct" && (
            <p className="mt-4 text-xl font-semibold text-green-600">
              Correct!
            </p>
          )}
          {feedback === "incorrect" && (
            <p className="mt-4 text-xl font-semibold text-red-600">
              Try again.
            </p>
          )}
        </div>
      )}

      <GuidedTour
        steps={tourStepsMissingNumber()}
        isRunning={isTourRunning}
        onComplete={handleTourComplete}
        currentStep={currentTourStep}
        onStepChange={setCurrentTourStep}
        tourName="missingNumber"
      />
    </div>
  );
};

export default MissingNumber;
